/**
 * UserRole Entity — Pure Domain Logic (TypeScript)
 *
 * Role hierarchy, permission checks, tab/location access.
 * ZERO Firebase dependency.
 */

import { USER_ROLE, ADMIN_ONLY_TABS } from '@core/constants';
import type { RoleResolution, AuthRole, UserRoleType } from '@core/types';

export class UserRole {
  role: AuthRole;
  ownerId: string;
  message: string;
  needsBootstrap: boolean;
  staffName: string | null;
  userRole: UserRoleType | null;
  assignedLocationId: string | null;
  managerPermissions: string[];
  managerLocationIds: string[];
  managerDoc: Record<string, unknown> | null;
  deactivated: boolean;

  constructor(data: Partial<RoleResolution> = {}) {
    this.role = data.role ?? null;
    this.ownerId = data.ownerId || '';
    this.message = data.message || '';
    this.needsBootstrap = data.needsBootstrap === true;
    this.staffName = data.staffName ?? null;
    this.userRole = data.userRole ?? null;
    this.assignedLocationId = data.assignedLocationId ?? null;
    this.managerPermissions = data.managerPermissions || [];
    this.managerLocationIds = data.managerLocationIds || [];
    this.managerDoc = (data.managerDoc as Record<string, unknown>) ?? null;
    this.deactivated = data.deactivated === true;
  }

  // ==========================================
  // ROLE CHECKS
  // ==========================================

  isOwner(): boolean { return this.role === USER_ROLE.OWNER; }
  isManager(): boolean { return this.role === USER_ROLE.MANAGER; }
  isStaff(): boolean { return this.role === 'staff'; }
  isAuthenticated(): boolean { return this.role != null && !this.deactivated; }
  isDeactivated(): boolean { return this.deactivated === true; }
  requiresBootstrap(): boolean { return this.needsBootstrap === true; }

  // ==========================================
  // PERMISSIONS
  // ==========================================

  canAccessTab(tabName: string): boolean {
    if (this.isOwner()) return true;
    if (this.isManager()) {
      if (this.managerPermissions.length > 0) {
        return this.managerPermissions.includes(tabName) || !(ADMIN_ONLY_TABS as readonly string[]).includes(tabName);
      }
      return true;
    }
    return !(ADMIN_ONLY_TABS as readonly string[]).includes(tabName);
  }

  canAccessLocation(locationId: string): boolean {
    if (this.isOwner()) return true;
    if (this.isManager() && this.managerLocationIds.length > 0) {
      return this.managerLocationIds.includes(locationId);
    }
    if (this.isStaff() && this.assignedLocationId) {
      return this.assignedLocationId === locationId;
    }
    return true;
  }

  requiresStepUp(tabName: string): boolean {
    if (!this.isOwner()) return false;
    return tabName === 'hrm' || tabName === 'financial';
  }

  // ==========================================
  // MAPPING
  // ==========================================

  static mapToUserRole(roleStr: string | undefined | null): UserRoleType {
    if (!roleStr || typeof roleStr !== 'string') return USER_ROLE.STAFF;
    const r = roleStr.trim().toLowerCase();
    if (r === 'admin' || r === 'area_manager') return USER_ROLE.AREA_MANAGER;
    if (r === 'branch_manager') return USER_ROLE.BRANCH_MANAGER;
    if (r.includes('khu vực') || r.includes('area')) return USER_ROLE.AREA_MANAGER;
    if (r.includes('chi nhánh') || r.includes('branch') || r.includes('quản lý')) return USER_ROLE.BRANCH_MANAGER;
    return USER_ROLE.STAFF;
  }

  // ==========================================
  // SERIALIZATION
  // ==========================================

  toData(): RoleResolution {
    return {
      role: this.role, ownerId: this.ownerId, message: this.message,
      needsBootstrap: this.needsBootstrap, staffName: this.staffName,
      userRole: this.userRole, assignedLocationId: this.assignedLocationId,
      managerPermissions: this.managerPermissions, managerLocationIds: this.managerLocationIds,
      deactivated: this.deactivated,
    };
  }

  static fromData(data: RoleResolution): UserRole {
    return new UserRole(data);
  }
}

export default UserRole;
